import { useCallback, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { ChecklistItem } from "../types/task";
import { apiV1Endpoint } from "../../../../api/v1";
import { message } from "antd";

interface UseUpdateChecklistItemProps {
  checklist: ChecklistItem[];
  setChecklist: React.Dispatch<React.SetStateAction<ChecklistItem[]>>;
}

interface UseUpdateChecklistItemResult {
  updateChecklistItem: (
    selectedTask: ChecklistItem,
    remarks?: string,
    patchToServer?: boolean
  ) => Promise<ChecklistItem | null>;
}

/**
 * Hook for updating checklist item (local + optional API patch)
 */
export const useUpdateCheckListItem = ({
  checklist,
  setChecklist,
}: UseUpdateChecklistItemProps): UseUpdateChecklistItemResult => {
  const { bookingDetails } = useSelector((state: any) => state.newBooking);
  const [isloading, setLoading] = useState<boolean>(false);
  const updateChecklistItem = useCallback(
    async (
      selectedTask: ChecklistItem,
      remarks?: string,
      patchToServer: boolean = false
    ): Promise<ChecklistItem | null> => {
      let updatedTask: ChecklistItem | null = null;

      // ✅ 1. Update locally
      setChecklist((prev) =>
        prev.map((task) => {
          if (task._id === selectedTask._id) {
            updatedTask = {
              ...task,
              status: "completed",
              completionDate: new Date().toISOString(),
              remarks: remarks || task.remarks,
            };
            updatedTask["leaseId"] = bookingDetails?.leaseId;
            updatedTask["check"] = true;
            return updatedTask;
          }
          return task;
        })
      );

      // ✅ 2. Optionally send patch request
      if (patchToServer && updatedTask) {
        setLoading(true);
        try {
          await axios.put(`${apiV1Endpoint}/leasing/checkListItemUpdate`, updatedTask);
          setLoading(false);
        } catch (error) {
          setLoading(false);
          message?.error(error?.message || "");
          console.error("❌ Failed to patch checklist item:", error);
        }
      }

      return updatedTask;
    },
    [checklist, setChecklist]
  );

  return { updateChecklistItem };
};
